// nosemgrep: dont_use_react_native_hooks
import { useLayoutEffect, useState } from 'react'
import { useMatches } from 'react-router-dom'

import { Observable } from '@apollo/client'

// nosemgrep: typescript.factorial-frontend.design-system-outside-dependencies
import {
  Match,
  ModuleRouteHandle,
  ResourceRouteHandle,
  RouteHandle,
} from 'modules/core/lib/navigation/routes'
// nosemgrep: typescript.factorial-frontend.design-system-outside-dependencies
import { ModuleMeta, ResourceMeta, RouteMeta } from 'modules/core/lib/navigation/types'

import { useMetaContext } from '../matches'
import { combineLatestObservables } from './utils'

type GenericHandleData<T, H, M> = {
  type: T
  path: string
  handle: H
  meta: M | undefined
  loading: boolean
  error: boolean
}

export type RouteHandleData = GenericHandleData<'route', RouteHandle, RouteMeta>
export type ModuleHandleData = GenericHandleData<'module', ModuleRouteHandle, ModuleMeta>
export type ResourceHandleData = GenericHandleData<'resource', ResourceRouteHandle, ResourceMeta>

export type HandleData = RouteHandleData | ModuleHandleData | ResourceHandleData

export type ResolvedHandleData =
  | { type: 'route'; meta?: RouteMeta; handle: RouteHandle; path: string }
  | { type: 'resource'; meta?: ResourceMeta; handle: ResourceRouteHandle; path: string }
  | { type: 'module'; meta?: ModuleMeta; handle: ModuleRouteHandle; path: string }

function buildData(
  type: 'route' | 'module' | 'resource',
  handle: RouteHandle | ModuleRouteHandle | ResourceRouteHandle,
  {
    loading,
    error,
    path,
    meta,
  }: { loading: boolean; error: boolean; path: string; meta?: RouteMeta | ModuleMeta }
): HandleData {
  if (type === 'module' && 'module' in handle) {
    return {
      type: 'module',
      loading,
      error,
      path,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      handle: handle as ModuleRouteHandle,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      meta: meta as ModuleMeta,
    }
  }

  if (type === 'resource' && 'resource' in handle && handle.resource === true) {
    return {
      type: 'resource',
      loading,
      error,
      path,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      handle: handle as ResourceRouteHandle,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      meta: meta as ResourceMeta,
    }
  }

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return {
    type: 'route',
    loading,
    error,
    path,
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    handle: handle as RouteHandle,
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    meta: meta as RouteMeta,
  }
}

// This function is used to get the metadata for the current page in order to render the
// breadcrumbs, page title, and other relevant metadata. It will traverse the matches from
// React Router and get metadata for each.
//
// Since this metadata can be async, we need to handle that. The `handle` property
// on routes can't use hooks, so data can't be easily reactive. We want it to be reactive
// because it probably comes from a GraphQL query, and we want metadata to update as
// Apollo's cache changes.
//
// For that reason, `handle` is a function that can return an observable (besides a
// plain value or a promise). This way we can make sure that updates to the data are
// reflected in the UI.
//
// In order to keep things sane, we turn everything into observables, and then we combine
// them with `combineLatestObservables` so that we only have to deal with a single array
// with the latest value of each observable.
export function useHandles(): {
  handles: HandleData[]
  resolvedHandles: ResolvedHandleData[]
  resolving: boolean
} {
  // There's no way to escape this as the matches are not typed.
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const matches = useMatches() as Match[]
  const { context } = useMetaContext()
  const [handles, setHandles] = useState<HandleData[]>([])

  const [resolvedHandles, setResolvedHandles] = useState<ResolvedHandleData[]>([])

  useLayoutEffect(() => {
    const observables: (Observable<HandleData> | HandleData | undefined)[] = matches.map(
      (match) => {
        const meta = match.handle?.meta
        const handle = match.handle
        const pendingMeta = meta?.(context)

        if (!handle) return undefined

        const type = getHandleType(handle)

        // Set loading and error states if the meta is an observable
        if (pendingMeta instanceof Observable) {
          return new Observable<HandleData>((observer) => {
            observer.next(
              buildData(type, handle, {
                loading: true,
                error: false,
                path: match.pathname,
              })
            )

            const subscription = pendingMeta.subscribe({
              next: (value) => {
                observer.next(
                  buildData(type, handle, {
                    loading: false,
                    error: false,
                    meta: value,
                    path: match.pathname,
                  })
                )
              },
              error: (err) => {
                observer.next(
                  buildData(type, handle, {
                    loading: false,
                    error: true,
                    path: match.pathname,
                  })
                )
                observer.error(err)
              },
              complete: () => {
                observer.complete()
              },
            })
            return () => subscription.unsubscribe()
          })
        }

        // Set loading and error states if the meta is a Promise
        if (pendingMeta instanceof Promise) {
          return new Observable<HandleData>((observer) => {
            observer.next(
              buildData(type, handle, {
                loading: true,
                error: false,
                path: match.pathname,
              })
            )
            pendingMeta.then(
              (value) => {
                observer.next(
                  buildData(type, handle, {
                    loading: false,
                    error: false,
                    meta: value,
                    path: match.pathname,
                  })
                )
                observer.complete()
              },
              (error) => {
                observer.next(
                  buildData(type, handle, {
                    loading: false,
                    error: true,
                    path: match.pathname,
                  })
                )
                observer.error(error)
              }
            )
          })
        }

        // Fake an observable otherwise
        return new Observable<HandleData>((observer) => {
          observer.next(
            buildData(type, handle, {
              loading: false,
              error: false,
              meta: pendingMeta,
              path: match.pathname,
            })
          )
          observer.complete()
        })
      }
    )

    const combinedObservables = combineLatestObservables(
      observables.filter((o): o is Observable<HandleData> => o !== undefined)
    )

    const subscription = combinedObservables.subscribe((result) => {
      const observableHandles = result
        .filter(Boolean)
        .sort((a, b) => (b.path.length < a.path.length ? 0 : -1))

      setHandles(observableHandles.filter(Boolean))

      if (observableHandles.every((handle) => !handle.loading && !handle.error)) {
        setResolvedHandles(observableHandles)
      }
    })

    return () => subscription.unsubscribe()
  }, [matches, context, setHandles])

  const resolving =
    handles.length !== resolvedHandles.length &&
    handles.some((handle) => handle.loading || handle.error)

  return { handles, resolvedHandles, resolving }
}

const getHandleType = (h: RouteHandle | ModuleRouteHandle | ResourceRouteHandle) => {
  if ('module' in h) {
    return 'module'
  }
  if ('resource' in h && h.resource) {
    return 'resource'
  }
  return 'route'
}
